import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RepositoryContext } from '../../store/RepositoryProvider';
import { UserContext } from '../../store/UserProvider';
import NavBar from '../../components/NavBar';
import ReportFilterPaginate, { SHOW_MODE } from '../../components/ReportFilterPaginate';
import API from '../../server/api';
import Table, { ALIGN_TABLE } from '../../components/Table';
import InfoDescription from '../../components/InfoDescription';
import Badge, { BADGE_TYPE } from '../../components/Badge';
import DateFormat from '../../components/DateFormat';
import Phone from '../../components/Phone';
import log from '../../utils/logger';
import Modal from '../../components/Modal';
import OrderInfo from '../order/order_info';
import Info from '../../components/Info';
import { LINE_MODE } from '../../components/Field';
import {OrderStatus, SellerType} from "../../models/types";

const ORDER_STATUS = {
  paid: BADGE_TYPE.SUCCESS,
  awaiting_payment: BADGE_TYPE.WARN,
  cancelled: BADGE_TYPE.ERROR,
  completed: BADGE_TYPE.INFO,
  awaiting_assignment: BADGE_TYPE.INFO
};

const MySales = () => {
  const {t} = useTranslation(['common', 'sale', 'order']);

  const userInfo = useContext(UserContext);
  const repository = useContext(RepositoryContext);
    const [sessionAccount] = userInfo.useSession('account');

  const [showOrderInfo, setShowOrderInfo] = useState(false);
  const [order, setOrder] = useState(null);
  const [, setClient] = useState(null);
  const [date, setDate] = useState(null);

  const handleCloseOrderInfo = useCallback(() => {
    setShowOrderInfo(false);
    setOrder(null);
  }, []);

  const handleOpenOrderInfo = useCallback((order) => {
    setOrder(order);
    setShowOrderInfo(true);
  }, []);

  useEffect(() => {
  }, []);

  const autocompleteTemplate = (item) => {
    let params = item.split(',');
    return <Fragment>{params[0]}<span style={{'display': 'none'}}>,</span><div className="small">{params[1]}</div></Fragment>;
  };

  const autocompleteHandler = async (query) => {
    return repository.list(API.order.autocomplete, { query }, Object).then((rows) => {
      let list = [];
      if (rows) {
        for (let i = 0; i < rows.length; ++i) {
          list.push({
            value: rows[i].value,
            title: `${rows[i].label}, ${t('sale:my_sales.phone', { phone: Phone.Text({ phone: rows[i].meta.phone }) })}`,
          });
        }
      }
      return list;
    }).catch((e) => {
      log.error(e);
      return Promise.reject(e);
    });
  };


    return (
    <div className="trip-app">
      <NavBar title={t('sale:my_sales.title')} />
      <div className="trip-app-body">
        <ReportFilterPaginate
          apiHandler={(reportParams) => {
            setClient(reportParams.filters.user);
            setDate(reportParams.filters.date);
            return userInfo.getMyOrders(reportParams);
          }}
          show={SHOW_MODE.ALWAYS}
          autosuggest={{
            key: 'user',
            template: autocompleteTemplate,
            apiHandler: autocompleteHandler
          }}
          sort={(a, b) => a.date < b.date ? 1 : (a.date > b.date ? -1 : 0)}
          placeholder={t('sale:my_sales.filter')}
          output={(list) => (
            <Table cols={2}>
              {list.map((order) => (
                <Fragment key={order.id}>
                  <Table.Row>
                    <Table.Cell onClick={() => { handleOpenOrderInfo(order); }}>
                      <InfoDescription description={<>{t('sale:my_sales.from_date')}<DateFormat.DateTime date={order.date} tz={true}/></>}>
                        <div className="d-fle align-items-center mb-2">
                            {t('sale:my_sales.order', { order: order.number })}
                            {order.account.id !== sessionAccount && (
                                <Badge type={BADGE_TYPE.INFO} className="ms-2 top">
                                    {order.account.title}
                                </Badge>
                            )}
                        </div>
                      </InfoDescription>
                    </Table.Cell>
                    <Table.Cell align={ALIGN_TABLE.RIGHT}>
                      <Badge type={ORDER_STATUS[order.status]}>{t(`sale:status.${order.status}`)}</Badge>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row className="pb-0" onClick={() => { handleOpenOrderInfo(order); }}>
                    <Table.Cell className="small">
                      {t('sale:my_sales.excursion')}
                    </Table.Cell>
                    <Table.Cell className="small" align={ALIGN_TABLE.RIGHT}>
                      <Info>{order.excursion.title}</Info>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row className="pt-0 pb-0" onClick={() => { handleOpenOrderInfo(order); }}>
                    <Table.Cell className="small">
                      {t('sale:my_sales.departure')}
                    </Table.Cell>
                    <Table.Cell className="small" align={ALIGN_TABLE.RIGHT}>
                      <Info><DateFormat.DateTime date={order.schedule?.date}/></Info>
                    </Table.Cell>
                  </Table.Row>
                  <Table.Row delim={true} className="mb-1" />
                </Fragment>
              ))}
            </Table>
          )}
          filters={[
              { key: 'number', title: t('sale:filter.order'), type: 'value.number', mode: LINE_MODE.TWO },
              { key: 'date', title: t('sale:filter.schedule.date'), type: 'value.date', value: date, erase: true },
              { key: 'status', title: t('sale:filter.status'), type: 'badge', list: [
                { id: OrderStatus.AWAITING_PAYMENT, title: t('sale:status.awaiting_payment') },
                { id: OrderStatus.PAID, title: t('sale:status.paid') },
                { id: OrderStatus.CANCELLED, title: t('sale:status.cancelled') },
                { id: OrderStatus.COMPLETED, title: t('sale:status.completed') }
              ] },
              { key: 'seller.type', title: t('sale:filter.sale_type'), type: 'badge', list: [
                { id: SellerType.CASHIER, title: t('sale:sale_type.own') },
                { id: SellerType.AGENT, title: t('sale:sale_type.stranger') },
              ] },
          ]}
        />
      </div>

      <Modal show={order && showOrderInfo} onHide={handleCloseOrderInfo}>
        <Modal.Header closeButton>
          <InfoDescription
            description={(
              <>
                {t('order:order_info.create')}
                {order && (<DateFormat.DateTime date={order.date} tz={true}/>)}
              </>
            )}
          >
            {t('order:order_info.title', { order: order && order.number })}
          </InfoDescription>
        </Modal.Header>
        <Modal.Body>
          {showOrderInfo && ( /* re-create body*/
            <OrderInfo order={order} onNext={handleCloseOrderInfo} onClose={handleCloseOrderInfo} />
          )}
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default MySales;